import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ComponentType, JSX, ReactNode } from 'react';
import { FICHE_PERSO_LIBELLE, estIdDeFichePerso, idDeFichePerso } from '@taki/data-model';
import { Button, IconBrowserCode2, IconTextFile, VueHtmlIsolee, cardDefsOf, carteDeVue, cellToText, clefsDuChoixMultiple } from '@taki/ui';
import type {
  Cell,
  EcranDeclare as EcranDeclareUi,
  EditorKind,
  Option,
  ProjectedRow,
  ViewColumn,
  ViewProps,
  ViewType,
} from '@taki/ui';
import { clefDeSurfaceDeVue } from './vues-de-fiche-donnees.js';
import type { DonneesDeVueHtml } from './vues-de-fiche-donnees.js';
import type { AppScreen } from '@taki/data-model';
import type { FicheGenerique } from './app-declaree.js';
import type { ActionsDeVueDeFiche, PorteurDActions } from './actions-de-vue-de-fiche.js';
import { AtelierEnLigne, FenetreDeFichePerso } from './AtelierDeFichePerso.js';
import type { ContexteDeFichePerso } from './AtelierDeFichePerso.js';

/**
 * LES VUES QUE L'APP DECLAREE APPORTE AU SELECTEUR, en plus de celles du socle
 * (chantier `fiche-suivi`, `docs/socle/fiche-suivi.md`, section 3) :
 *
 * - une vue CANVAS (G23, accordee dans `canvas.views` de `taki.app.json`) :
 *   sa carte d'identite dans `vuesCustom` de `taki.screens.json`, son rendu par
 *   le composant `canvas['vue:<id>']` du manifeste. Au niveau `collection` elle
 *   recoit ce qu'une vue du socle recoit ; au niveau `record`, la fiche ouverte ;
 * - une vue HTML ISOLEE (`ecran.vuesHtml`), deposee par le MCP : son code est de
 *   la DONNEE, rendu dans le cadre ferme du socle (`VueHtmlIsolee`), nourri de la
 *   fiche reduite aux champs que la vue declare.
 *
 * L'UNE ET L'AUTRE SONT DES `ViewType` ORDINAIRES : le moteur (`useScreen`) les
 * propose, les grise, les ouvre et les garde dans l'adresse comme les vues du
 * socle, le `ViewHost` les monte dans son filet. Rien n'est double.
 */

/**
 * CE QU'UNE VUE CANVAS RECOIT (`canvas['vue:<id>']` du manifeste). Tout ce
 * qu'une vue du socle recoit (`ViewProps`, `docs/VUES-CONTRAT.md` section 6 :
 * `rows`, `onOpenRecord`, `onPatchRecord`...), plus les fiches BRUTES, la porte,
 * et au niveau fiche la fiche ouverte, son ecriture et la sortie.
 *
 * UN MOTEUR CUSTOM S'Y MONTE comme dans une porte Canvas : `useMoteurVanilla`
 * (`@taki/app-runtime`) rend une `ref` a poser sur l'element hote, et rien
 * dans ces props ne l'empeche.
 */
export interface ProprietesDeVueCanvas extends ViewProps {
  niveau: 'collection' | 'record';
  /** L'id de la porte active. */
  porte: string;
  /** Les fiches de la porte, brutes, telles que le chargeur les a rendues. */
  fiches: readonly FicheGenerique[];
  /** Au niveau fiche : la fiche ouverte, brute ; `null` ailleurs (ou introuvable). */
  fiche: FicheGenerique | null;
  /**
   * Ecrit un champ de la fiche ouverte, par la CLEF DE COLONNE (`budget`,
   * `status` pour un natif) : le meme chemin que l'edition en place
   * (optimisme, annulation, `saveState`). Rejette hors du niveau fiche.
   */
  onPatch(champ: string, valeur: Cell): Promise<void>;
  /** Quitte la fiche (la puce de sortie). */
  sortir(): void;
  /**
   * AU NIVEAU FICHE : LA FICHE OUVERTE TELLE QUE LA « FICHE » DU SOCLE LA
   * RESOUT (`lectureDeLaFiche`) ; `null` au niveau collection et sans fiche.
   * C'est ce qu'une vue de fiche Canvas LIT pour montrer les memes informations
   * que la Fiche, autrement : jamais la declaration de l'org relue par l'app.
   */
  lecture: LectureDeLaFiche | null;
  /**
   * POSE LES ACTIONS DE LA FICHE DANS LA BARRE DU GABARIT ; `null` LES RETIRE.
   * Rappelee a chaque changement d'etat. Une vue de fiche ne porte AUCUN bouton
   * d'action dans son corps : `rangee` (tous les gestes visibles, sans plafond) va a la rangee 3
   * de la fiche (`recordActions`), `menu` en tete du menu « ... » de la fiche,
   * avant les gestes du socle. Les actions ne valent que pour CETTE vue, active,
   * et la fiche ouverte : elles tombent au retour a « Fiche », a l'ouverture
   * d'une autre fiche et au demontage. Jamais sur un brouillon. Sans effet au
   * niveau collection. Une vue qui ne l'appelle pas ne change rien.
   */
  poserActions(actions: ActionsDeVueDeFiche | null): void;
}

/**
 * UN CHAMP DE LA FICHE OUVERTE, tel que la Fiche le montre : une colonne du
 * binding de sa `core.fiche`, et la cellule que le moteur en a projetee.
 */
export interface ChampDeLaFiche {
  /** La clef de colonne, NUE (`budget`, `startAt` pour un natif) : celle d'`onPatch`. */
  cle: string;
  /** Le libelle de la Fiche (celui de la colonne, regle au Studio). */
  libelle: string;
  /** L'editeur declare (`select`, `date`...), absent quand la Fiche le deduit de la valeur. */
  type?: EditorKind;
  /** Le chemin de stockage (`customFields.budget`) ; absent pour un natif. */
  chemin?: string;
  /** Le jeu que la valeur designe, quand c'est l'id d'une autre fiche. */
  reference?: ViewColumn['reference'];
  /** La valeur STOCKEE, telle que la projection la rend (un id pour une reference). */
  valeur: Cell;
  /**
   * CE QUE LA FICHE AFFICHE, tel quel : l'habillage de la colonne (« 1 h 30 »),
   * le mot du choix, le nom de la fiche designee (par les annuaires charges).
   * `null` : le champ est vide, la Fiche y ecrit « Non renseigné ».
   */
  rendu: ReactNode | null;
  /** Le meme, en TEXTE (pour un titre, une infobulle, un test) ; `null` quand vide. */
  texte: string | null;
  /**
   * LES CHOIX ACTIFS DU CHAMP, dans l'ordre du catalogue, avec leurs couleurs :
   * une option archivee n'y est plus, sauf si la fiche la porte encore (meme
   * regle que l'editeur de la Fiche). Vide pour un champ sans catalogue.
   */
  options: readonly Option[];
  /**
   * LES CHOIX QUE LA FICHE PORTE (un pour une liste, plusieurs pour un choix
   * multiple), resolus dans le catalogue complet, archives compris : de quoi
   * poser une pastille de la bonne couleur. Une valeur hors catalogue y entre
   * avec son mot brut et le ton neutre.
   */
  choisies: readonly Option[];
}

/** Une carte de champs de la Fiche, dans l'ordre de sa declaration. */
export interface CarteDeLaFiche {
  titre: string;
  champs: readonly ChampDeLaFiche[];
}

/** LA FICHE OUVERTE, RESOLUE PAR LA FICHE : son titre, son statut, ses champs et ses cartes. */
export interface LectureDeLaFiche {
  /** Le titre de la Fiche (role `title`, deja mis en texte, jamais vide). */
  titre: string;
  /** Le statut (role `status`), resolu dans son catalogue ; `null` sans statut. */
  statut: Option | null;
  /** Les champs du binding, dans son ordre. Un champ masque par le jeu n'y est pas. */
  champs: readonly ChampDeLaFiche[];
  /** Un champ par son nom (`budget`, `socle:dueAt` ou `dueAt`), `undefined` s'il n'est pas montre. */
  champ(nom: string): ChampDeLaFiche | undefined;
  /** Les cartes de champs de la Fiche (`cartes`), sans les cartes liees, les totaux ni les fichiers. */
  cartes: readonly CarteDeLaFiche[];
}

/** Ce que l'ecran prete aux vues qu'il monte : la porte, ses fiches, et de quoi nourrir une vue HTML. */
export interface ContexteDesVuesDeFiche {
  porte: string;
  fiches: readonly FicheGenerique[];
  fiche: FicheGenerique | null;
  /**
   * Les donnees d'une vue HTML pour la fiche ouverte (`donneesDeVueHtml`), `null`
   * sans fiche. `lecture` est la Fiche telle que la vue la lit (`lectureDeLaFiche`) :
   * elle donne ce que la Fiche AFFICHE d'une reference ou d'une lecture
   * (`affichesDeLaFiche`). Absente, les donnees sont celles d'avant.
   */
  donneesDe(
    vue: { champs: readonly string[]; liens?: readonly string[] | undefined },
    lecture?: LectureDeLaFiche | null,
  ): DonneesDeVueHtml | null;
  /** L'apercu d'un brouillon, quand on en regarde un. */
  apercu: ApercuDeVueHtml | null;
  /** Pose (ou retire, avec `null`) les actions d'une vue de fiche dans la barre du gabarit. */
  poserActions(porteur: PorteurDActions, actions: ActionsDeVueDeFiche | null): void;
  /** Ce que l'atelier de la fiche personnalisee lit, pour la porte active ; `null` si elle ne l'active pas. */
  fichePerso: ContexteDeFichePerso | null;
}

/** L'apercu d'une vue HTML en brouillon (`?apercu=`), et son geste. */
export interface ApercuDeVueHtml {
  viewId: string;
  label: string;
  html: string;
  champs: readonly string[];
  liens?: readonly string[];
  /** Publie le brouillon ; rejette avec la raison. */
  publier(): Promise<void>;
  /** Vrai pour qui ne peut pas publier (le bouton s'eteint avec sa raison). */
  raison?: string;
}

export const ContexteDeVues = createContext<ContexteDesVuesDeFiche | null>(null);

/* ------------------------------------------------------------------ */
/* Les descripteurs                                                    */
/* ------------------------------------------------------------------ */

const TAILLE_D_ICONE = 15;

/* ------------------------------------------------------------------ */
/* La lecture de la fiche ouverte : ce que la Fiche a deja resolu      */
/* ------------------------------------------------------------------ */

const PREFIXE_NATIF = 'socle:';
/** Le titre de la carte unique d'une Fiche sans cartes declarees (`DEFAULT_CARD_TITLE`, `RecordView.tsx`). */
const CARTE_PAR_DEFAUT = 'Informations';

/** Le meme test de vide que la Fiche (`hasNode`, `views/core/RecordView.tsx`). */
const rendVide = (rendu: ReactNode): boolean => rendu === null || rendu === undefined || rendu === '' || rendu === false;

/**
 * LA FICHE OUVERTE, LUE DANS CE QUE LE MOTEUR A PROJETE POUR ELLE (chantier
 * `fiche-suivi`, audit I11). Une vue de fiche Canvas sans binding propre lit
 * celui de la `core.fiche` de sa porte (`avecLesVuesDeFiche`, `assemblage.ts`) :
 * ses `columns`, la ligne ouverte (`rows[0]` au niveau fiche), `optionsOf` et
 * le sac `options.cards` sont donc EXACTEMENT ceux de la Fiche.
 *
 * LE PROBLEME QU'ELLE FERME. Avant elle, Projets et Timesheet relisaient la
 * declaration de l'org eux-memes pour nommer leurs champs : une instance
 * invalide (que l'ecran avait ecartee) s'y lisait quand meme, un champ masque
 * par le jeu s'y montrait, une option archivee s'y proposait. Ici rien n'est
 * relu : la declaration a deja ete resolue, validee, recalee sur les jeux et
 * privee de ses champs masques avant que le binding n'existe ; les libelles
 * sont ceux des colonnes, les mots ceux des cellules (habillages et annuaires
 * compris), les choix ceux du catalogue que l'editeur propose.
 *
 * LES CARTES se lisent dans le sac du binding PAR LA LECTURE DE LA FICHE
 * (`cardDefsOf` de `@taki/ui`, la seule) : une carte que la Fiche ignore,
 * celle-ci l'ignore aussi. Seules les clefs de champs y comptent ; un total,
 * une carte liee, les fichiers ont leur place dans la Fiche et ne se recopient
 * pas. Un champ n'est que dans la premiere carte qui le cite, comme dans la
 * Fiche (`buildCards`).
 *
 * PURE ET EXPORTEE pour un test : un champ qui disparait d'une vue ne fait
 * rougir aucun build.
 */
export function lectureDeLaFiche(
  props: Pick<ViewProps, 'rows' | 'columns' | 'optionsOf' | 'options'>,
  ligne: ProjectedRow | undefined = props.rows[0],
): LectureDeLaFiche | null {
  if (ligne === undefined) return null;
  const champs = props.columns.map((colonne): ChampDeLaFiche => {
    const cellule = ligne.cells[colonne.key];
    const valeur: Cell = cellule?.value ?? null;
    const noeud: ReactNode = cellule?.node;
    const vide = cellule === undefined || rendVide(noeud);
    /* LE CATALOGUE DE LA FICHE : celui de la cellule (un catalogue propre a
       cette fiche) d'abord, celui de la colonne ensuite, comme l'editeur. */
    const catalogue = cellule?.options ?? props.optionsOf(colonne.key);
    const posees =
      colonne.kind === 'multiselect' ? clefsDuChoixMultiple(valeur) : valeur === null || valeur === '' ? [] : [String(valeur)];
    const choisies = catalogue.length === 0
      ? []
      : posees.map(
          (cle) => catalogue.find((option) => option.value === cle) ?? { value: cle, label: cle, tone: 'neutral' as const },
        );
    return {
      cle: colonne.key,
      libelle: colonne.label,
      ...(colonne.kind === undefined ? {} : { type: colonne.kind }),
      ...(colonne.path === undefined ? {} : { chemin: colonne.path }),
      ...(colonne.reference === undefined ? {} : { reference: colonne.reference }),
      valeur,
      rendu: vide ? null : noeud,
      texte: vide
        ? null
        : typeof noeud === 'string' || typeof noeud === 'number'
          ? String(noeud)
          : cellToText(cellule) || null,
      options: catalogue.filter((option) => option.archived !== true || posees.includes(option.value)),
      choisies,
    };
  });
  const parCle = new Map(champs.map((champ) => [champ.cle, champ]));
  const champ = (nom: string): ChampDeLaFiche | undefined =>
    parCle.get(nom.startsWith(PREFIXE_NATIF) ? nom.slice(PREFIXE_NATIF.length) : nom);
  const declarees = cardDefsOf(props.options);
  const prises = new Set<string>();
  const cartes = declarees.flatMap((carte): CarteDeLaFiche[] => {
    if (carte.kind !== 'fields') return [];
    const siens = carte.keys.flatMap((cle) => {
      const trouve = typeof cle === 'string' && !prises.has(cle) ? parCle.get(cle) : undefined;
      if (trouve === undefined) return [];
      prises.add(cle as string);
      return [trouve];
    });
    return siens.length === 0 ? [] : [{ titre: carte.title, champs: siens }];
  });
  /* SANS CARTE DECLAREE (aucune entree valide), la Fiche range tous ses champs
     dans une carte unique, « Informations » (`buildCards`) : la lecture fait de
     meme. */
  const sansDeclaration = declarees.length === 0 && champs.length > 0;
  return {
    titre: ligne.title,
    statut: ligne.status ?? null,
    champs,
    champ,
    cartes: sansDeclaration ? [{ titre: CARTE_PAR_DEFAUT, champs }] : cartes,
  };
}

/**
 * LA FICHE OUVERTE, lue comme la Fiche la lit, pour une vue de fiche : memorisee
 * sur ce que la projection rend (une ligne neuve, un binding neuf). `null` hors
 * d'une fiche ouverte.
 */
function useLectureDeLaFiche(props: ViewProps): LectureDeLaFiche | null {
  const { rows, columns, optionsOf, options } = props;
  return useMemo(() => lectureDeLaFiche({ rows, columns, optionsOf, options }), [rows, columns, optionsOf, options]);
}

/**
 * POSE LES ACTIONS D'UNE VUE DE FICHE, au nom de cette vue et de cette fiche
 * (`poserActions` de `ProprietesDeVueCanvas`) ; elles sont retirees au demontage
 * et quand la fiche change.
 */
function usePoserLesActions(idDeLaVue: string, ouverte: string | null): (actions: ActionsDeVueDeFiche | null) => void {
  const contexte = useContext(ContexteDeVues);
  const poserDuContexte = contexte?.poserActions;
  const poser = useCallback(
    (actions: ActionsDeVueDeFiche | null): void => {
      if (ouverte == null) return;
      poserDuContexte?.({ vue: idDeLaVue, fiche: ouverte }, actions);
    },
    [poserDuContexte, idDeLaVue, ouverte],
  );
  useEffect(
    () => () => {
      if (ouverte != null) poserDuContexte?.({ vue: idDeLaVue, fiche: ouverte }, null);
    },
    [poserDuContexte, idDeLaVue, ouverte],
  );
  return poser;
}

/** Le corps d'une vue Canvas : le contexte de l'ecran, puis le composant de l'app. */
function corpsDeVueCanvas(
  Composant: ComponentType<ProprietesDeVueCanvas>,
  niveau: 'collection' | 'record',
  idDeLaVue: string,
): ComponentType<ViewProps> {
  function VueCanvas(props: ViewProps): JSX.Element {
    const contexte = useContext(ContexteDeVues);
    const ouverte = niveau === 'record' ? props.cursor.openId : null;
    /* LES ACTIONS, au nom de cette vue et de cette fiche : l'ecran ne les rend
       que pour le couple (vue active, fiche ouverte), et elles sont retirees au
       demontage ou quand la fiche change. */
    const poserDuContexte = contexte?.poserActions;
    const poserActions = useCallback(
      (actions: ActionsDeVueDeFiche | null): void => {
        if (ouverte == null) return;
        poserDuContexte?.({ vue: idDeLaVue, fiche: ouverte }, actions);
      },
      [poserDuContexte, ouverte],
    );
    useEffect(
      () => () => {
        if (ouverte != null) poserDuContexte?.({ vue: idDeLaVue, fiche: ouverte }, null);
      },
      [poserDuContexte, ouverte],
    );
    const { rows, columns, optionsOf, options } = props;
    const lecture = useMemo(
      () => (niveau === 'record' ? lectureDeLaFiche({ rows, columns, optionsOf, options }) : null),
      [rows, columns, optionsOf, options],
    );
    return createElement(Composant, {
      ...props,
      niveau,
      porte: contexte?.porte ?? '',
      fiches: contexte?.fiches ?? [],
      fiche: niveau === 'record' ? (contexte?.fiche ?? null) : null,
      onPatch: (champ: string, valeur: Cell) =>
        ouverte == null
          ? Promise.reject(new Error("une vue de collection n'a pas de fiche ouverte à écrire"))
          : props.onPatchColumn(ouverte, champ, valeur),
      sortir: () => props.onOpenRecord(null),
      lecture,
      poserActions,
    });
  }
  VueCanvas.displayName = 'VueCanvas';
  return VueCanvas;
}

/**
 * LES VUES CANVAS DE L'ECRAN, en descripteurs : une par entree de `vuesCustom`
 * qui a son composant. La verification des deux sens (une carte sans
 * composant, un composant sans carte) est faite au montage
 * (`definirAppDeclaree`) : ici on ne monte que ce qui se repond.
 */
export function vuesCanvasDeLEcran(
  ecran: EcranDeclareUi,
  canvas: Readonly<Record<string, unknown>>,
): ViewType[] {
  return (ecran.vuesCustom ?? []).flatMap((declaree) => {
    const Composant = canvas[clefDeSurfaceDeVue(declaree.id)] as ComponentType<ProprietesDeVueCanvas> | undefined;
    if (Composant === undefined) return [];
    const carte = carteDeVue(ecran, declaree.id);
    const Component = corpsDeVueCanvas(Composant, carte.level, declaree.id);
    const type: ViewType = {
      id: declaree.id as ViewType['id'],
      ...carte,
      icon: createElement(IconTextFile, { size: TAILLE_D_ICONE }),
      load: () => Promise.resolve({ Component }),
    };
    return [type];
  });
}

/** Le bandeau d'un apercu : il le dit, et il propose de publier. */
function BandeauDApercu({ apercu }: { apercu: ApercuDeVueHtml }): JSX.Element {
  const [etat, setEtat] = useState<{ enCours: boolean; erreur: string | null }>({ enCours: false, erreur: null });
  const publier = (): void => {
    setEtat({ enCours: true, erreur: null });
    apercu.publier().then(
      () => setEtat({ enCours: false, erreur: null }),
      (cause: unknown) => setEtat({ enCours: false, erreur: cause instanceof Error ? cause.message : String(cause) }),
    );
  };
  const raison = apercu.raison ?? (etat.enCours ? 'Publication en cours' : undefined);
  return (
    <div
      className="flex shrink-0 items-center gap-3 border-b border-accent-border bg-accent-tint px-4 py-2 text-[0.8125rem] text-accent-ink"
      data-testid="bandeau-apercu"
    >
      <span className="min-w-0 flex-1 truncate">
        {etat.erreur === null ? 'Aperçu, visible de toi seul' : `Échec de la publication : ${etat.erreur}`}
      </span>
      <Button
        variant="secondary"
        size="sm"
        onClick={publier}
        disabled={raison !== undefined}
        {...(raison === undefined ? {} : { title: raison })}
      >
        Publier
      </Button>
    </div>
  );
}

/** Une vue HTML, telle que la declaration de l'org la porte. */
interface VueHtmlLue {
  id: string;
  label: string;
  html: string;
  champs: readonly string[];
  liens?: readonly string[] | undefined;
  version?: number | undefined;
}

/**
 * « ÉDITER LA VUE CANVAS » : sur la fiche personnalisee PUBLIEE, pour qui peut
 * deposer, un geste en tete du menu « ... » de la fiche (`poserActions`, jamais
 * un bouton dans la vue) qui ouvre la fenetre de l'atelier. Rien ne se rend
 * pour les autres.
 */
function EditionDeLaVue({ vue, ouverte, lecture }: { vue: VueHtmlLue; ouverte: string | null; lecture: LectureDeLaFiche | null }): JSX.Element | null {
  const contexte = useContext(ContexteDeVues);
  const poser = usePoserLesActions(vue.id, ouverte);
  const [fenetre, setFenetre] = useState(false);
  const fichePerso = contexte?.fichePerso ?? null;
  const peut = fichePerso !== null && fichePerso.peutDeposer;
  /* Rappele a chaque rendu : l'ecran ne se redessine que si l'affichage change. */
  useEffect(() => {
    poser(peut ? { menu: [{ id: 'editer-vue-canvas', libelle: 'Éditer la vue Canvas', onClick: () => setFenetre(true) }] } : null);
  });
  if (!peut || contexte == null || ouverte == null) return null;
  return (
    <FenetreDeFichePerso
      entree={{ fichePerso, ficheId: ouverte, lecture, donneesDe: contexte.donneesDe }}
      ouverte={fenetre}
      onClose={() => setFenetre(false)}
    />
  );
}

/** Le cadre ferme d'une vue HTML : la fiche reduite, l'apercu s'il y en a un, et, pour la fiche personnalisee publiee, « Éditer la vue Canvas ». */
function CadreDeVue({ vue, props, perso }: { vue: VueHtmlLue; props: ViewProps; perso: boolean }): JSX.Element {
  const contexte = useContext(ContexteDeVues);
  const apercu = contexte?.apercu?.viewId === vue.id ? contexte.apercu : null;
  const montree = apercu ?? vue;
  const lecture = useLectureDeLaFiche(props);
  const donnees = contexte?.donneesDe(montree, lecture) ?? null;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {apercu === null ? null : <BandeauDApercu apercu={apercu} />}
      <div className="min-h-0 flex-1">
        <VueHtmlIsolee html={montree.html} donnees={donnees} titre={montree.label} />
      </div>
      {perso ? <EditionDeLaVue vue={vue} ouverte={props.cursor.openId ?? null} lecture={lecture} /> : null}
    </div>
  );
}

/** Le corps d'une vue HTML : la fiche reduite, dans le cadre ferme. */
function corpsDeVueHtml(vue: VueHtmlLue): ComponentType<ViewProps> {
  function VueHtml(props: ViewProps): JSX.Element {
    return <CadreDeVue vue={vue} props={props} perso={false} />;
  }
  VueHtml.displayName = 'VueHtml';
  return VueHtml;
}

/** L'atelier pour qui peut deposer, un etat sobre pour les autres. */
function AtelierOuEtatSobre({ idDeLaVue, props }: { idDeLaVue: string; props: ViewProps }): JSX.Element {
  const contexte = useContext(ContexteDeVues);
  const lecture = useLectureDeLaFiche(props);
  const ouverte = props.cursor.openId ?? null;
  const poser = usePoserLesActions(idDeLaVue, ouverte);
  const fichePerso = contexte?.fichePerso ?? null;
  if (fichePerso === null || !fichePerso.peutDeposer || contexte == null || ouverte == null) {
    return <p className="px-6 py-8 text-sm text-ink-500">Vue non publiée</p>;
  }
  return (
    <div className="px-6 py-6">
      <AtelierEnLigne entree={{ fichePerso, ficheId: ouverte, lecture, donneesDe: contexte.donneesDe }} poserActions={poser} />
    </div>
  );
}

/**
 * LE CORPS UNIQUE DE LA FICHE PERSONNALISEE D'UNE PORTE, publiee ou non. Le
 * module d'une vue est mis en cache PAR ID (`useViewModule`) : deux corps pour le
 * meme id laisseraient l'atelier a l'ecran apres la publication. Celui-ci lit
 * donc l'etat courant dans le contexte de l'ecran : la vue publiee (le cadre
 * ferme, a jour de sa derniere version), l'apercu d'un brouillon, ou l'atelier.
 */
function corpsDeFichePerso(idDeLaVue: string): ComponentType<ViewProps> {
  function VueFichePerso(props: ViewProps): JSX.Element {
    const contexte = useContext(ContexteDeVues);
    const publiee = contexte?.fichePerso?.publiee ?? null;
    const apercu = contexte?.apercu?.viewId === idDeLaVue ? contexte.apercu : null;
    if (publiee !== null) return <CadreDeVue vue={{ ...publiee, id: idDeLaVue }} props={props} perso />;
    if (apercu !== null) return <CadreDeVue vue={{ ...apercu, id: idDeLaVue }} props={props} perso={false} />;
    return <AtelierOuEtatSobre idDeLaVue={idDeLaVue} props={props} />;
  }
  VueFichePerso.displayName = 'VueFichePerso';
  return VueFichePerso;
}

/** Un descripteur de vue HTML : une vue de fiche, son defilement a elle (le cadre). */
export function typeDeVueHtml(vue: VueHtmlLue): ViewType {
  /* La fiche personnalisee a UN corps pour tous ses etats (le cache des modules est par id). */
  const Component = estIdDeFichePerso(vue.id) ? corpsDeFichePerso(vue.id) : corpsDeVueHtml(vue);
  return {
    id: vue.id as ViewType['id'],
    label: vue.label,
    icon: createElement(IconBrowserCode2, { size: TAILLE_D_ICONE }),
    level: 'record',
    demand: 'window',
    scroll: 'self',
    load: () => Promise.resolve({ Component }),
  };
}

/**
 * LA FICHE PERSONNALISEE D'UNE PORTE QUI N'A PAS DE HTML : une vue de fiche dont
 * le corps est l'atelier. `arrivee: false` : elle ne sert jamais d'arrivee, une
 * fiche ne s'ouvre pas sur une invitation, seul le selecteur (ou une adresse
 * qui la nomme) l'ouvre.
 */
function typeDeFichePersoNonPubliee(id: string): ViewType {
  const Component = corpsDeFichePerso(id);
  return {
    id: id as ViewType['id'],
    label: FICHE_PERSO_LIBELLE,
    icon: createElement(IconBrowserCode2, { size: TAILLE_D_ICONE }),
    level: 'record',
    arrivee: false,
    demand: 'window',
    scroll: 'zone',
    load: () => Promise.resolve({ Component }),
  };
}

/**
 * LES VUES QUE L'APP APPORTE, en descripteurs : ses vues Canvas (collection et
 * fiche) et ses vues HTML. L'ecran et ses reglages les passent a
 * `assemblerEcran`, qui les range par porte.
 */
export function vuesDeLApp(
  ecran: AppScreen,
  canvas: Readonly<Record<string, unknown>>,
): ViewType[] {
  return [...vuesCanvasDeLEcran(ecran, canvas), ...vuesHtmlDeLEcran(ecran)];
}

/**
 * Les vues HTML de l'ecran, en descripteurs : les vues ordinaires, puis, pour
 * chaque porte qui active la fiche personnalisee (`fichePerso`) et montre un
 * jeu, UNE vue d'id `idDeFichePerso(porte)` : le cadre ferme si une vue HTML de
 * cet id est publiee, l'atelier sinon. Une vue `html.perso-*` publiee SANS porte
 * qui l'active (orpheline) n'est offerte nulle part ; une seule source par id.
 */
export function vuesHtmlDeLEcran(
  ecran: {
    vuesHtml?: readonly VueHtmlLue[] | undefined;
    portes?: readonly { id: string; fichePerso?: true | undefined; jeu?: unknown }[] | undefined;
  },
): ViewType[] {
  const publiees = ecran.vuesHtml ?? [];
  const ordinaires = publiees.filter((vue) => !estIdDeFichePerso(vue.id)).map((vue) => typeDeVueHtml(vue));
  const personnalisees = (ecran.portes ?? []).flatMap((porte): ViewType[] => {
    if (porte.fichePerso !== true || porte.jeu === undefined) return [];
    const id = idDeFichePerso(porte.id);
    const publiee = publiees.find((vue) => vue.id === id);
    return [publiee === undefined ? typeDeFichePersoNonPubliee(id) : typeDeVueHtml(publiee)];
  });
  return [...ordinaires, ...personnalisees];
}
